/**
 * The band that says this is a demo, with its controls: make the next apply fail, connect or
 * disconnect a server, start over. Rendered by the shell only when the API in use is the demo
 * backend (it has a `demo` member).
 */
import { LitElement, html } from 'lit';

import { REFRESH_EVENT } from '../../lib/poll/index.js';
import { api } from '../api/index.js';
import { forgetViewer } from '../viewer.js';
import { planeDemoBarStyles } from '../../styles/components/plane-demo-bar.js';

class PlaneDemoBar extends LitElement {
  static properties = {
    open: { state: true },
  };

  static styles = planeDemoBarStyles;

  constructor() {
    super();
    this.open = false;
  }

  changed() {
    this.requestUpdate();
    document.body.dispatchEvent(new CustomEvent(REFRESH_EVENT));
  }

  render() {
    const demo = api.demo;
    const servers = demo.servers();
    return html`
      <div class="bar" role="region" aria-label="Demo controls">
        <strong>Demo</strong>
        <span class="note">No backend: servers and clawlets are simulated in this browser.</span>
        <button class="toggle" aria-expanded=${this.open ? 'true' : 'false'} aria-controls="demo-controls" @click=${() => (this.open = !this.open)}>
          ${this.open ? 'Hide controls' : 'Controls'}
        </button>
        <div class="controls ${this.open ? 'open' : ''}" id="demo-controls">
          <label>
            View as
            <select
              @change=${(event) => {
                demo.setViewAs(event.target.value);
                forgetViewer();
                // the shell, the navigation and every page change: start from the overview
                window.location.assign('/');
              }}
            >
              <option value="" ?selected=${!demo.viewAs()}>operator</option>
              ${demo.customerNames().map((name) => html`<option value=${name} ?selected=${demo.viewAs() === name}>${name}</option>`)}
            </select>
          </label>
          <label>
            Next apply
            <select
              @change=${(event) => {
                demo.failNextApply(event.target.value);
                this.changed();
              }}
            >
              <option value="" ?selected=${!demo.failNext()}>succeeds</option>
              ${Object.entries(demo.failureModes).map(
                ([mode, description]) => html`<option value=${mode} ?selected=${demo.failNext() === mode} title=${description}>fails: ${mode}</option>`,
              )}
            </select>
          </label>
          <span class="servers">
            ${servers.map(
              (server) => html`
                <button
                  title=${server.connection === 'online' ? `Disconnect ${server.id}` : `Connect ${server.id}`}
                  @click=${() => {
                    demo.setConnected(server.id, server.connection !== 'online');
                    this.changed();
                  }}
                >
                  ${server.connection === 'online' ? '⏻' : '○'} ${server.id}
                </button>
              `,
            )}
          </span>
          <button
            @click=${() => {
              demo.reset();
              this.changed();
            }}
          >
            Reset demo
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('plane-demo-bar', PlaneDemoBar);
